import { useEffect, useState, type ComponentType } from "react";

/**
 * Loads Vercel Speed Insights after idle so it stays off the LCP / main-thread
 * critical path. The import is wrapped in try/catch so the build does not fail
 * if @vercel/speed-insights is not installed in the current build environment.
 */
export function DeferredSpeedInsights() {
  const [Comp, setComp] = useState<ComponentType | null>(null);

  useEffect(() => {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") {
      return;
    }

    let cancelled = false;

    const run = () => {
      // Optional dependency: build the specifier at runtime and use a
      // Function-wrapped dynamic import so Rollup never sees the module path
      // during static analysis. If the package isn't installed, the import
      // rejects and we skip quietly.
      const specifier = "@vercel/speed-insights" + "/react";
      const dynamicImport = new Function("s", "return import(s)") as (
        s: string
      ) => Promise<{ SpeedInsights?: ComponentType }>;
      void dynamicImport(specifier)
        .then((m) => {
          if (!cancelled && m?.SpeedInsights) {
            setComp(() => m.SpeedInsights);
          }
        })
        .catch(() => {
          // Speed Insights not available in this environment — silently skip.
        });
    };

    type IdleWin = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const w = window as IdleWin;

    let idleId: number | undefined;
    const t = window.setTimeout(run, 2000);
    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(run, { timeout: 4000 });
    }

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
    };
  }, []);

  if (!Comp) return null;
  return <Comp />;
}
